import { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { extractYouTubeId } from '../utils/youtube';
import { loadYouTubeIframeApi, type YouTubePlayer } from '../utils/youtubePlayer';

/** Responsive 16:9 YouTube embed. Fires `onEnded` when the viewer plays the video to the end. */
export default function ModuleVideo({
  url,
  onEnded,
}: {
  url?: string | null;
  onEnded?: () => void;
}) {
  const videoId = extractYouTubeId(url);
  const containerRef = useRef<HTMLDivElement>(null);
  // Hold the latest callback in a ref so the player isn't torn down and rebuilt on every
  // parent render (the player effect below intentionally depends only on the video id).
  const onEndedRef = useRef(onEnded);
  useEffect(() => {
    onEndedRef.current = onEnded;
  }, [onEnded]);

  // The iframe is created imperatively inside the React-owned container: YT.Player.destroy()
  // removes the iframe it was attached to from the DOM, so a React-rendered iframe would leave
  // React reconciling a detached node after the first videoId change. This effect owns the
  // iframe's whole lifecycle instead.
  useEffect(() => {
    const container = containerRef.current;
    if (!videoId || !container) return;

    // enablejsapi + origin let the IFrame API attach to this iframe and exchange messages securely.
    const origin = window.location.origin;
    const iframe = document.createElement('iframe');
    iframe.src =
      `https://www.youtube.com/embed/${videoId}?enablejsapi=1&rel=0` +
      (origin ? `&origin=${encodeURIComponent(origin)}` : '');
    iframe.title = 'Learning module video';
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
    iframe.allowFullscreen = true;
    Object.assign(iframe.style, { position: 'absolute', top: '0', left: '0', width: '100%', height: '100%', border: '0' });
    container.appendChild(iframe);

    let player: YouTubePlayer | undefined;
    let cancelled = false;

    loadYouTubeIframeApi()
      .then(YT => {
        if (cancelled) return;
        player = new YT.Player(iframe, {
          events: {
            onStateChange: event => {
              if (event.data === YT.PlayerState.ENDED) onEndedRef.current?.();
            },
          },
        });
      })
      .catch(() => {
        // API blocked or offline — the video still plays; we just can't auto-complete.
      });

    return () => {
      cancelled = true;
      try {
        player?.destroy();
      } catch {
        /* player may already be gone if the iframe unmounted first */
      }
      iframe.remove(); // no-op if destroy() already detached it
    };
  }, [videoId]);

  if (!videoId) {
    return (
      <Box sx={{ borderRadius: 1, bgcolor: 'action.hover', p: 3, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">No video</Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{ position: 'relative', width: '100%', pt: '56.25%', borderRadius: 1, overflow: 'hidden', bgcolor: '#000' }}
    />
  );
}
